import Luiv3Constants from '@app/lui3/constants';
import {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {
  fetchOutlookPeopleFromEmailAddresses,
  type OutlookPeopleFromEmailAddressesJson,
  type OutlookPersonFromEmailJson,
} from '../../../requests';
import {usePodRequest} from '../../../usePodRequest';
import {
  emailMatchesSet,
  normalizedEmailKey,
  normalizeEmailAddress,
  normalizeEmailVariants,
} from './emailNormalization';
import LoxoRecipients from './LoxoRecipients';
import OtherRecipients from './OtherRecipients';
import {
  type OutlookRecipient,
  useOutlookEmailRecipients,
} from './useOutlookEmailRecipients';

interface Props {
  agencyId: number;
}

const Wrapper = styled.div`
  padding: 1rem;
  display: flex;
  gap: 1rem;
  flex-direction: column;
`;

const TotalCount = styled.div`
  font-size: 0.875rem;
  color: ${Luiv3Constants.baseColor['gray-1000']};
  font-weight: 700;
  line-height: 1.25rem;
  letter-spacing: 0.01em;
`;

const isAbortError = (error: unknown): boolean => {
  return error instanceof DOMException && error.name === 'AbortError';
};

const dedupeRecipientsByEmail = (
  recipients: OutlookRecipient[]
): OutlookRecipient[] => {
  const seen = new Set<string>();
  return recipients.filter((recipient) => {
    const key = normalizedEmailKey(recipient.email);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const EmailRecipients = ({agencyId}: Props) => {
  const {request} = usePodRequest();
  const {recipients, isMessageContext} = useOutlookEmailRecipients();
  const [syncableEmailAddresses, setSyncableEmailAddresses] = useState<
    string[]
  >([]);
  const [loxoPeople, setLoxoPeople] = useState<OutlookPersonFromEmailJson[]>(
    []
  );

  useEffect(() => {
    let activeController: AbortController | undefined;
    let latestRequestId = 0;

    const load = () => {
      const requestId = latestRequestId + 1;
      latestRequestId = requestId;
      const emails = [
        ...new Set(
          recipients.map((r) => normalizeEmailAddress(r.email)).filter(Boolean)
        ),
      ];
      if (emails.length === 0) {
        activeController?.abort();
        activeController = undefined;
        setSyncableEmailAddresses([]);
        setLoxoPeople([]);
        return;
      }

      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;

      fetchOutlookPeopleFromEmailAddresses(request, agencyId, emails, {
        signal: controller.signal,
      })
        .then((data: OutlookPeopleFromEmailAddressesJson) => {
          if (!controller.signal.aborted && requestId === latestRequestId) {
            setSyncableEmailAddresses(data.email_addresses_for_sync);
            setLoxoPeople(data.people);
          }
        })
        .catch((error: unknown) => {
          if (
            !controller.signal.aborted &&
            requestId === latestRequestId &&
            !isAbortError(error)
          ) {
            setSyncableEmailAddresses([]);
            setLoxoPeople([]);
          }
        });
    };

    load();

    let refetchDebounce: number | undefined;
    const scheduleRefetch = () => {
      if (refetchDebounce != null) {
        window.clearTimeout(refetchDebounce);
      }
      refetchDebounce = window.setTimeout(() => {
        refetchDebounce = undefined;
        load();
      }, 100);
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        scheduleRefetch();
      }
    };
    const onFocus = () => {
      scheduleRefetch();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      activeController?.abort();
      if (refetchDebounce != null) {
        window.clearTimeout(refetchDebounce);
      }
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [agencyId, recipients, request]);

  const syncableEmailSet = useMemo(() => {
    return new Set(syncableEmailAddresses.flatMap(normalizeEmailVariants));
  }, [syncableEmailAddresses]);

  const syncableRecipients = useMemo(() => {
    return dedupeRecipientsByEmail(
      recipients.filter((r) => emailMatchesSet(r.email, syncableEmailSet))
    );
  }, [recipients, syncableEmailSet]);

  const otherRecipients = useMemo(() => {
    const loxoEmails = new Set(
      loxoPeople.flatMap((p) =>
        p.emails.flatMap((e) => normalizeEmailVariants(e.value))
      )
    );
    return syncableRecipients.filter(
      (recipient) => !emailMatchesSet(recipient.email, loxoEmails)
    );
  }, [loxoPeople, syncableRecipients]);

  const recipientCount = useMemo(() => {
    return otherRecipients.length + loxoPeople.length;
  }, [otherRecipients, loxoPeople]);

  if (!isMessageContext) {
    return null;
  }

  return (
    <Wrapper>
      <TotalCount>
        {recipientCount} Other {recipientCount === 1 ? 'Person' : 'People'} in
        this Email
      </TotalCount>
      <LoxoRecipients
        loxoPeople={loxoPeople}
        syncableEmailAddresses={syncableEmailAddresses}
      />
      <OtherRecipients recipients={otherRecipients} agencyId={agencyId} />
    </Wrapper>
  );
};

export default EmailRecipients;
